import {createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef} from "react";
import {PriceLine2, PriceRider} from "../../../../api/Pricing";
import {EventEmitter, formatCents, useAsync2} from "nate-react-api-helpers";
import {useProjectId} from "../ProjectName";
import {useChangesetId} from "./ChangesetId";
import {api} from "../../../../api/API";
import {getAll} from "../../../logistics/routes/useGetAll";
import {green, red} from "@mui/material/colors";
import {css} from "@emotion/css";
import {MiscPrice} from "../../../../api/MiscPricing";

export const RunningTotalContext = createContext({
    capture: (input: Promise<PriceLine2[]>, miscP: Promise<MiscPrice[]>, riderP: Promise<PriceRider[]>): Promise<PriceLine2[]>  =>{
        // do nothing, invalid context
        return input;
    },
    total: new EventEmitter<number>(),
})

export function RunningTotalProvider(props: PropsWithChildren<{}>) {

    const projectId = useProjectId();
    const sessionId = useChangesetId();
    const total = useMemo(() => new EventEmitter<number>(), []);
    const existingPrices = useRef<PriceLine2[]>([]);
    const existingMiscPrices = useRef<MiscPrice[]>([]);
    const existingRiderPrices = useRef<PriceRider[]>([]);

    useEffect(() => {
        if(!total.lastValue) total.emit(0);
    }, [total]);

    const ctx = useMemo(() => {
        return {
            capture: async (prices: Promise<PriceLine2[]>, miscP: Promise<MiscPrice[]>, riderP: Promise<PriceRider[]>): Promise<PriceLine2[]> => {
                const input = await prices;
                const miscInput = await miscP;
                const riderInput = await riderP;

                existingPrices.current = existingPrices.current.filter(v => !input.some(v2 => v2.id === v.id));
                existingPrices.current.push(...input);

                existingMiscPrices.current = existingMiscPrices.current.filter(v => !miscInput.some(v2 => v2.id === v.id));
                existingMiscPrices.current.push(...miscInput);

                existingRiderPrices.current = existingRiderPrices.current.filter(v => !riderInput.some(v2 => v2.id === v.id));
                existingRiderPrices.current.push(...riderInput);

                const totalAmt = calcTotal(existingPrices.current, existingMiscPrices.current, existingRiderPrices.current);

                if(total.lastValue !== totalAmt) {
                    total.emit(totalAmt);
                }

                return input;
            },
            total: total,
        }
    }, [total]);


    useAsync2(async (input) => {
        const misc = getAll(null, offset => api.miscPricing.list({...input, offset}), {useLazy: true});
        const prices = getAll(null, offset => api.pricing.list({...input, offset, previewChangeset: input.previewChangeset}))
        const riders = getAll(null, offset => api.pricing.listRiders({...input, offset}), {useLazy: true})

        return ctx.capture(prices, misc, riders);
    }, {
        project: projectId,
        previewChangeset: sessionId,
        filterByChangeset: true,
    }, [projectId, sessionId]);

    return <RunningTotalContext.Provider value={ctx}>
        {props.children}
    </RunningTotalContext.Provider>
}

export function calcTotal(rows: PriceLine2[], misc: MiscPrice[], riders: PriceRider[], priceOnlyMode: boolean = false): number {
    const priceTotal = rows.reduce((acc, line) => {
        if(priceOnlyMode) {
            return acc + (line.extendedPrice - (line.backup?.extendedPrice || 0));
        }

        if(line.refunded) {
            return acc - line.refundAmount;
        }

        return acc + line.extendedPrice
    }, 0)

    const miscTotal = misc.reduce((acc, line) => {
        if(line.backup) {
            return acc + (line.extendedPrice - line.backup.extendedPrice);
        }

        return acc + line.extendedPrice;
    }, 0);

    const riderTotal = riders.reduce((acc, line) => {
        if(line.backup) {
            return acc + (line.extendedPrice - line.backup.extendedPrice);
        }

        return acc + line.extendedPrice
    }, 0);

    return priceTotal + miscTotal + riderTotal;
}

export function LiveTotal() {
    const ctx = useContext(RunningTotalContext);
    const total = EventEmitter.reactValue(ctx.total);

    if(total === undefined || total === null) return "calculating..."

    return (
        <div style={{
            display: "flex",
            fontSize: "1rem",
            alignItems: "center",
        }}>
            <div style={{marginRight: 8}}>Total: {formatCents(Math.abs(total))}</div>
            {total < 0 ? <div className={chipCss} style={{backgroundColor: red["700"]}}>Credit</div> : <div style={{backgroundColor: green["700"]}} className={chipCss}>Extra</div>}
        </div>
    )
}

const chipCss = css({
    color: "white", padding: 2, paddingLeft: 8, paddingRight: 8,
    borderRadius: 4,
    fontSize: "0.8rem",
})